/**
 * If this library is imported, it will emulate the Odin native environment.
 *   - navTo - opens Focus Flow & Nav Flows in new tabs
 *   - navClose - closes the current tab
 *   - openResource - will work
 *
 *  Usage:
 *
 *   In the root of your McApp project include
 *
 *   // DEV MODE ONLY
 *   import { nativeEmulator } from '@origin-digital/native-features';
 *   if (__DEV__) nativeEmulator();
 *
 *   ensure isNative() is true, either :
 *    - add ?native=true to the query string
 *    - change the user-agent to include 'odin-mobile-app'
 *
 */

import { logger } from "@origin-digital/reporting-client";
import { LOG_TAG } from "./logTag";

import {
  FEATURES,
  IFeatureBase,
  IFetchAccessTokenFeature,
  IHttpReqError,
  INavExternalAuthFeature,
  INavExternalFeature,
  INavFocusFeature,
  INavModalFeature,
  INavStandardFeature,
  INavTabFeature,
  IOpenResourceFeature,
  IReauthenticateFeature,
  ISpinnerFeature,
  IViewPdfFeature,
  IToastFeature,
  IShowModalFeature,
  ICloseModalFeature,
  ILinkEvAccountFeature,
  IDeviceWalletSupportedFeature,
  IDeviceWalletOpenFeature,
  IHideNativeTabBarFeature,
  ILaunchAppReviewFeature,
} from "./features.type";

export const NATIVE_QUERY_STRING_IDENTIFIER = "native";

// from dynamicConfig
const tabs = [
  {
    id: "dashboard",
    title: "Dashboard",
    actionBarTitle: "My Account",
    path: "/my",
    icon: "dashboard",
  },
  {
    id: "rewards",
    title: "Rewards",
    actionBarTitle: "Rewards",
    path: "/my/reward/rewards-and-offers",
    icon: "tag-heart",
    iconType: "materialCommunityIcons",
  },
  {
    id: "help",
    title: "Help",
    actionBarTitle: "Help & Support",
    path: "/help-support",
    icon: "help-outline",
  },
  {
    id: "more",
    title: "More",
    path: "",
    icon: "more-horiz",
  },
];

const features = {
  features: Object.values(FEATURES),
  appInfo: {
    version: "1.alpha.test",
    platform: "nativeEmulator",
    userAgent: "nativeEmulator; odin",
  },
};

const dispatchFeatureEvent = (): void => {
  document.dispatchEvent(new MessageEvent("message", { data: features }));
};

// https://stackoverflow.com/questions/5999118/how-can-i-add-or-update-a-query-string-parameter
function updateQueryString(key: string, value: string, url: string): string {
  const re = new RegExp(`([?&])${key}=.*?(&|#|$)(.*)`, "gi");
  let hash: string[];

  if (re.test(url)) {
    if (typeof value !== "undefined" && value !== null) {
      return url.replace(re, `$1${key}=${value}$2$3`);
    } else {
      hash = url.split("#");
      url = hash[0].replace(re, "$1$3").replace(/(&|\?)$/, "");
      if (typeof hash[1] !== "undefined" && hash[1] !== null) {
        url += `#${hash[1]}`;
      }
      return url;
    }
  } else if (typeof value !== "undefined" && value !== null) {
    const separator = url.indexOf("?") === -1 ? "?" : "&";
    hash = url.split("#");
    url = `${hash[0] + separator + key}=${value}`;
    if (typeof hash[1] !== "undefined" && hash[1] !== null) {
      url += `#${hash[1]}`;
    }
    return url;
  } else {
    return url;
  }
}

function addNativeEmu(url: string): string {
  return updateQueryString(NATIVE_QUERY_STRING_IDENTIFIER, "true", url);
}

const SAFE_URL_PROTOCOLS = ["http:", "https:"];
const WINDOW_OPEN_FEATURES = "noopener,noreferrer";

function isSafeUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url, window.location.href);
    return SAFE_URL_PROTOCOLS.includes(protocol);
  } catch {
    return false;
  }
}

function safeAssign(url: string): void {
  if (!isSafeUrl(url)) {
    logger.error(`${LOG_TAG} rejected unsafe url for assign`, { url });
    return;
  }
  window.location.assign(url);
}

function safeOpen(url: string, target?: string): void {
  if (!isSafeUrl(url)) {
    logger.error(`${LOG_TAG} rejected unsafe url for open`, { url });
    return;
  }
  window.open(url, target, WINDOW_OPEN_FEATURES);
}

const handler: Record<FEATURES, (data: any) => void> = {
  requestAppInfo: () => dispatchFeatureEvent(),
  navBack: () => history.back(),
  navExternal: (data: INavExternalFeature["data"]) => {
    safeOpen(data.url);
  },
  navExternalAuth: (data: INavExternalAuthFeature["data"]) => {
    safeOpen(data.url);
  },
  navFocus: (data: INavFocusFeature["data"]) => {
    if (data.show) {
      safeOpen(addNativeEmu(data.url), "focus");
    } else {
      window.close();
    }
  },
  navModal: (data: INavModalFeature["data"]) => {
    if (data.show) {
      safeOpen(addNativeEmu(data.url), "modal");
    } else {
      window.close();
    }
  },
  navTab: (data: INavTabFeature["data"]) => {
    const tab = tabs.find((t) => t.id === data.tab);
    if (tab) {
      safeOpen(addNativeEmu(tab.path), tab.id);
    }
  },
  navStandard: (data: INavStandardFeature["data"]) => {
    safeAssign(addNativeEmu(data.url));
  },
  navClose: () => {
    window.close();
  },
  openResource: (data: IOpenResourceFeature["data"]) => {
    safeOpen(addNativeEmu(data.url));
  },
  spinner: (data: ISpinnerFeature["data"]) =>
    logger.error(`${LOG_TAG} spinner not supported`, data),

  viewpdf: (data: IViewPdfFeature["data"]) => safeOpen(data.url),
  httpReqError: (data: IHttpReqError["data"]) =>
    logger.error(`${LOG_TAG} httpReqError not supported`, data),
  reauthenticate: (data: IReauthenticateFeature["data"]) =>
    logger.error(`${LOG_TAG} reauthenticate not supported`, data),
  fetchAccessToken: (data: IFetchAccessTokenFeature["data"]) =>
    logger.error(`${LOG_TAG} fetchAccessToken not supported`, data),
  logout: () => logger.error(`${LOG_TAG} logout not supported`),
  toast: (data: IToastFeature["data"]) =>
    logger.error(`${LOG_TAG} toast not supported`, data),
  showModal: (data: IShowModalFeature["data"]) =>
    logger.error(`${LOG_TAG} modal not supported`, data),
  closeModal: (data: ICloseModalFeature["data"]) =>
    logger.error(`${LOG_TAG} modal not supported`, data),
  asyncMessaging: () =>
    logger.error(`${LOG_TAG} asyncMessaging feature not supported`),
  linkEvAccount: (data: ILinkEvAccountFeature["data"]) =>
    logger.error(`${LOG_TAG} linkEvAccount feature not supported`, data),
  deviceWalletSupported: (data: IDeviceWalletSupportedFeature["data"]) =>
    logger.error(`${LOG_TAG} deviceWallet feature not supported`, data),
  deviceWalletOpen: (data: IDeviceWalletOpenFeature["data"]) =>
    logger.error(`${LOG_TAG} deviceWallet feature not supported`, data),
  fetchTheme: () => logger.error(`${LOG_TAG} fetchTheme feature not supported`),
  hideNativeTabBar: (data: IHideNativeTabBarFeature["data"]) =>
    logger.error(`${LOG_TAG} hideNativeTabBar feature not supported`, data),
  launchAppReview: (data: ILaunchAppReviewFeature["data"]) =>
    logger.error(`${LOG_TAG} launchAppReview feature not supported`, data),
};

let initialised = false;

export const initNativeEmulator = (): void => {
  if (initialised) return;
  initialised = true;
  logger.info(`${LOG_TAG} enabled`);
  window.addEventListener(
    "message",
    (event) => {
      // Only accept messages this page sent to itself (postMessage.ts uses
      // window.postMessage(msg, window.location.origin)). Ignore anything
      // from openers, parents, popups, or browser extensions.
      if (event.source !== window) return;

      if (event.data && typeof event.data === "string") {
        let payload: IFeatureBase;
        try {
          payload = (JSON.parse(event.data) || {}) as IFeatureBase;
        } catch {
          return;
        }
        const { action, data } = payload;
        if (!action) return;
        // hasOwnProperty guard prevents prototype-key dispatch
        // (e.g. action: "toString" / "constructor").
        if (Object.prototype.hasOwnProperty.call(handler, action)) {
          logger.debug(`${LOG_TAG} postMessage to odin`, payload);
          handler[action](data);
        } else {
          logger.debug(`${LOG_TAG} no native handler for action`, payload);
        }
        return;
      }
      if (
        (event.data && event.data.source === "react-devtools-bridge") ||
        (event.data && event.data.source === "react-devtools-content-script")
      ) {
        return;
      }
      logger.trace(`${LOG_TAG} message ignored`, event);
    },
    false
  );
  dispatchFeatureEvent();
};
